import RichTextToolbar from "@/components/eventEditor/RichTextToolbar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
    pillButtonClassName,
    pillPrimaryButtonClassName,
} from "@/components/vistassistant/assistantClasses";
import formatDuration from "@/components/vistassistant/formatDuration";
import draftToHtml from "@/components/writeMail/draftToHtml";
import AgendaItem from "@/domain/entities/agenda/AgendaItem";
import CreateAgendaItemData from "@/domain/entities/agenda/CreateAgendaItemData";
import UpdateAgendaItemData from "@/domain/entities/agenda/UpdateAgendaItemData";
import useCreateAgendaEvent from "@/hooks/agenda/useCreateAgendaEvent";
import useUpdateAgendaEvent from "@/hooks/agenda/useUpdateAgendaEvent";
import useSetEventEditor from "@/hooks/useSetEventEditor";
import useErrorToast from "@/hooks/utils/useErrorToast";
import {
    startOfCalendarDay,
    toAllDayApiEndDate,
    toAllDayDisplayEndDate,
} from "@/lib/agendaAllDayDates";
import { cn } from "@/lib/utils";
import { EditorContent } from "@tiptap/react";
import {
    ArrowLeft,
    CalendarClock,
    Check,
    House,
    Loader2,
    MapPin,
    NotebookPen,
} from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import createDateTimeString from "./createDateTimeString";
import EventAttendeesInput from "./EventAttendeesInput";
import EventDayPicker from "./EventDayPicker";
import { eventFormTone, type EventFormTone } from "./eventFormTone";
import EventTimePicker from "./EventTimePicker";

type EventEditFormProps = {
    event: AgendaItem;
    setIsEditing?: (isEditing: boolean) => void;
    onSuccess?: (created?: CreateAgendaItemData) => void;
    /**
     * The surface the form sits on: the calendar's purple modal (default, unchanged), or a white card
     * of the assistant's chat (`light`).
     */
    tone?: EventFormTone;
    /** `light` only: « Annuler », next to the save button. */
    onCancel?: () => void;
    /** `light` only: the save button's label (default: « Enregistrer »). */
    submitLabel?: string;
};

const LIGHT_LABEL = "text-base font-semibold text-primary-darkest";

const timeOn = (date: Date | undefined, time: string) => {
    if (!date) return undefined;
    const [hours, minutes] = time.split(":").map(Number);
    const at = new Date(date);
    at.setHours(hours, minutes, 0, 0);
    return at;
};

const EDITOR_CONTENT_CLASSES = [
    "w-full h-full",
    "cursor-text",
    "p-4",
    "text-start",
    "overflow-wrap-anywhere",
    "text-sm",
    "[&_.ProseMirror]:outline-none",
    "[&_ul]:list-disc [&_ul]:pl-4",
    "[&_ol]:list-decimal [&_ol]:pl-4",
].join(" ");

const EventEditForm = ({
    event,
    setIsEditing,
    onSuccess,
    tone = "dark",
    onCancel,
    submitLabel,
}: EventEditFormProps) => {
    const { t } = useTranslation();
    const light = tone === "light";
    const toneClasses = eventFormTone[tone];
    const showErrorToast = useErrorToast();

    // In the assistant (light look), the agenda is the model's plain text: escaped into paragraphs, as
    // « Créer l'événement » sends it, never read as HTML.
    const { editor } = useSetEventEditor({
        content:
            event?.body ||
            (light && event?.bodyPreview
                ? draftToHtml(event.bodyPreview)
                : event?.bodyPreview) ||
            "",
    });

    const [subject, setSubject] = useState<string>(event?.subject ?? "");
    const [startDate, setStartDate] = useState<Date | undefined>(
        event?.isAllDayEvent() && event.start
            ? startOfCalendarDay(event.start)
            : event?.start,
    );
    const [endDate, setEndDate] = useState<Date | undefined>(
        event && event?.isAllDayEvent() && event.end && event.start
            ? toAllDayDisplayEndDate(event.start, event.end)
            : event?.end,
    );
    const [startTime, setStartTime] = useState<string>(
        `${event?.start.getHours().toString().padStart(2, "0")}:${event?.start.getMinutes().toString().padStart(2, "0")}`,
    );
    const [endTime, setEndTime] = useState<string>(
        `${event?.end.getHours().toString().padStart(2, "0")}:${event?.end.getMinutes().toString().padStart(2, "0")}`,
    );
    const [isAllDay, setIsAllDay] = useState<boolean | undefined>(
        event?.isAllDayEvent(),
    );
    const [location, setLocation] = useState<string | undefined>(
        event?.location?.displayName,
    );
    const [attendees, setAttendees] = useState<string[]>(
        event?.attendees.map((attendee) => attendee.emailAddress.address) ?? [],
    );

    const submitted = useRef<CreateAgendaItemData | undefined>(undefined);
    const { updateAgendaEvent } = useUpdateAgendaEvent(() => onSuccess?.());
    const { createAgendaEvent } = useCreateAgendaEvent(() =>
        onSuccess?.(submitted.current),
    );

    const isLoading =
        updateAgendaEvent.isPending || createAgendaEvent.isPending;

    const handleFormSubmit = () => {
        const startDateTime = createDateTimeString(
            startDate,
            isAllDay ? "00:00" : startTime,
            !!isAllDay,
        );

        const endDateTime = createDateTimeString(
            isAllDay && endDate ? toAllDayApiEndDate(endDate) : endDate,
            isAllDay ? "00:00" : endTime,
            !!isAllDay,
        );

        if (!startDateTime || !endDateTime || !subject) {
            showErrorToast({
                titleTranslationKey: t("errors.agenda.missingProperties"),
                descriptionTranslationKey: t(
                    "errors.agenda.missingPropertiesDescription",
                ),
            });

            return;
        }

        if (startDateTime > endDateTime) {
            showErrorToast({
                titleTranslationKey: t("errors.agenda.endTimeBeforeStartTime"),
                descriptionTranslationKey: t(
                    "errors.agenda.endTimeBeforeStartTimeDescription",
                ),
            });

            return;
        }

        if (!event.isNewEvent()) {
            const updateAgendaItemData = new UpdateAgendaItemData({
                location,
                subject,
                timeUpdate:
                    startDateTime && endDateTime
                        ? {
                              start: startDateTime,
                              end: endDateTime,
                              isAllDay,
                          }
                        : undefined,
                attendees,
                description: editor?.getHTML(),
            });

            updateAgendaEvent.mutate({
                agendaItemId: event.id,
                updateAgendaItemData,
            });
        } else {
            const createAgendaItemData = new CreateAgendaItemData({
                location,
                subject,
                start: startDateTime,
                end: endDateTime,
                attendees,
                description: editor?.getHTML(),
                isAllDay,
            });

            submitted.current = createAgendaItemData;
            createAgendaEvent.mutate({
                createAgendaItemData,
            });
        }
    };

    if (light) {
        const sameDay =
            !!startDate &&
            !!endDate &&
            startDate.toDateString() === endDate.toDateString();
        const startAt = timeOn(startDate, startTime);
        const endAt = timeOn(endDate, endTime);
        const field = cn("border", toneClasses.field, toneClasses.border);
        const column = "flex min-w-0 flex-col gap-2";
        const grid =
            "grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-4";

        return (
            <form
                className="flex flex-col gap-5 text-primary-darkest"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleFormSubmit();
                }}
            >
                <div className={column}>
                    <Label className={LIGHT_LABEL} htmlFor="event-subject">
                        {t("dashboard.widgets.calendar.event.form.title")}
                    </Label>
                    <Input
                        className={cn(
                            field,
                            toneClasses.placeholder,
                            "md:text-base",
                        )}
                        id="event-subject"
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder={t(
                            "dashboard.widgets.calendar.event.subject",
                        )}
                        value={subject}
                    />
                </div>
                {!isAllDay && sameDay ? (
                    <div className={grid}>
                        <div className={column}>
                            <Label
                                className={LIGHT_LABEL}
                                htmlFor="start-date-picker"
                            >
                                {t(
                                    "dashboard.widgets.calendar.event.form.date",
                                )}
                            </Label>
                            <EventDayPicker
                                date={startDate}
                                id="start"
                                setDate={(date) => {
                                    setStartDate(date);
                                    setEndDate(date);
                                }}
                                tone={tone}
                            />
                        </div>
                        <div className={column}>
                            <p className={LIGHT_LABEL}>
                                {t(
                                    "dashboard.widgets.calendar.event.form.start",
                                )}
                            </p>
                            <EventTimePicker
                                setTime={setStartTime}
                                time={startTime}
                                tone={tone}
                            />
                        </div>
                        <div className={column}>
                            <p className={LIGHT_LABEL}>
                                {t("dashboard.widgets.calendar.event.form.end")}
                            </p>
                            <EventTimePicker
                                setTime={setEndTime}
                                time={endTime}
                                tone={tone}
                            />
                            {startAt && endAt && endAt > startAt && (
                                <p className="text-sm text-assistant-muted">
                                    {t(
                                        "dashboard.widgets.calendar.event.form.duration",
                                        {
                                            duration: formatDuration(
                                                startAt,
                                                endAt,
                                                t,
                                            ),
                                        },
                                    )}
                                </p>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className={grid}>
                        <div className={column}>
                            <Label
                                className={LIGHT_LABEL}
                                htmlFor="start-date-picker"
                            >
                                {t(
                                    "dashboard.widgets.calendar.event.form.start",
                                )}
                            </Label>
                            <EventDayPicker
                                date={startDate}
                                id="start"
                                setDate={setStartDate}
                                tone={tone}
                            />
                            {!isAllDay && (
                                <EventTimePicker
                                    setTime={setStartTime}
                                    time={startTime}
                                    tone={tone}
                                />
                            )}
                        </div>
                        <div className={column}>
                            <Label
                                className={LIGHT_LABEL}
                                htmlFor="end-date-picker"
                            >
                                {t("dashboard.widgets.calendar.event.form.end")}
                            </Label>
                            <EventDayPicker
                                date={endDate}
                                id="end"
                                setDate={setEndDate}
                                tone={tone}
                            />
                            {!isAllDay && (
                                <EventTimePicker
                                    setTime={setEndTime}
                                    time={endTime}
                                    tone={tone}
                                />
                            )}
                        </div>
                    </div>
                )}
                <div className={column}>
                    <p className={LIGHT_LABEL}>
                        {t("dashboard.widgets.calendar.event.form.attendees")}
                    </p>
                    <EventAttendeesInput
                        attendees={attendees.filter(
                            (attendee) =>
                                attendee !==
                                event?.organizer?.emailAddress.address,
                        )}
                        setAttendees={(values) => {
                            let newAttendees = values;
                            if (event?.organizer?.emailAddress.address) {
                                newAttendees = [
                                    event?.organizer?.emailAddress.address,
                                    ...values,
                                ];
                            }
                            setAttendees(newAttendees);
                        }}
                        tone={tone}
                    />
                </div>
                <div className={column}>
                    <Label className={LIGHT_LABEL} htmlFor="event-location">
                        {t("dashboard.widgets.calendar.event.form.location")}
                    </Label>
                    <div className="relative">
                        <House
                            aria-hidden
                            className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-primary-darkest"
                            strokeWidth={1.5}
                        />
                        <Input
                            className={cn(field, "pl-12 md:text-base")}
                            id="event-location"
                            onChange={(e) => {
                                e.stopPropagation();
                                setLocation(e.target.value);
                            }}
                            value={location}
                        />
                    </div>
                </div>
                {editor && (
                    <div className={column}>
                        <p className={LIGHT_LABEL}>
                            {t("dashboard.widgets.calendar.event.form.agenda")}
                        </p>
                        <div
                            className={cn(
                                "rounded-2xl border bg-white",
                                toneClasses.border,
                            )}
                        >
                            <EditorContent
                                className={cn(
                                    EDITOR_CONTENT_CLASSES,
                                    "px-4 py-3 text-base",
                                )}
                                editor={editor}
                            />
                        </div>
                    </div>
                )}
                <div className="flex items-center gap-3">
                    <Checkbox
                        checked={isAllDay}
                        className={toneClasses.checkbox}
                        id="all-day-checkbox"
                        onCheckedChange={() => setIsAllDay(!isAllDay)}
                    />
                    <Label
                        className="cursor-pointer text-base font-normal"
                        htmlFor="all-day-checkbox"
                    >
                        {t("dashboard.widgets.calendar.event.allDay")}
                    </Label>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                    {onCancel && (
                        <button
                            className={pillButtonClassName}
                            disabled={isLoading}
                            onClick={onCancel}
                            type="button"
                        >
                            {t("dashboard.widgets.calendar.event.cancel")}
                        </button>
                    )}
                    <button
                        className={pillPrimaryButtonClassName}
                        disabled={
                            !subject || !startDate || !endDate || isLoading
                        }
                        type="submit"
                    >
                        {isLoading ? (
                            <Loader2 aria-hidden className="animate-spin" />
                        ) : (
                            <Check aria-hidden strokeWidth={1.75} />
                        )}
                        {submitLabel ??
                            t("dashboard.widgets.calendar.event.save")}
                    </button>
                </div>
            </form>
        );
    }

    return (
        <form
            className="flex h-full flex-col justify-between gap-6 py-2"
            onSubmit={(e) => {
                e.preventDefault();
                handleFormSubmit();
            }}
        >
            <ScrollArea className="my-1 mr-1 flex-1 px-6 py-2">
                <div className="flex max-h-[650px] flex-1 flex-col gap-6">
                    <div className="flex items-center gap-2 pr-6">
                        {!event.isNewEvent() && (
                            <Button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsEditing?.(false);
                                }}
                                variant="invisible"
                            >
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                        )}
                        <Input
                            className={cn(
                                "border-b-1 rounded-none border-l-0 border-r-0 border-t-0 pl-1 focus-visible:ring-0",
                                toneClasses.placeholder,
                                "md:text-xl",
                            )}
                            onChange={(e) => setSubject(e.target.value)}
                            placeholder={t(
                                "dashboard.widgets.calendar.event.subject",
                            )}
                            value={subject}
                        />
                    </div>
                    <div>
                        <div className="flex gap-3">
                            <CalendarClock
                                className={cn(
                                    "mt-2.5 h-4 w-4 flex-shrink-0",
                                    toneClasses.icon,
                                )}
                            />
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-wrap items-center gap-2">
                                    <div className="flex items-center gap-2">
                                        <EventDayPicker
                                            date={startDate}
                                            id="start"
                                            setDate={setStartDate}
                                            tone={tone}
                                        />

                                        {!isAllDay && (
                                            <EventTimePicker
                                                setTime={setStartTime}
                                                time={startTime}
                                                tone={tone}
                                            />
                                        )}
                                    </div>
                                    <div>-</div>
                                    <div className="flex items-center gap-2">
                                        <EventDayPicker
                                            date={endDate}
                                            id="end"
                                            setDate={setEndDate}
                                            tone={tone}
                                        />
                                        {!isAllDay && (
                                            <EventTimePicker
                                                setTime={setEndTime}
                                                time={endTime}
                                                tone={tone}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Checkbox
                                        checked={isAllDay}
                                        className={toneClasses.checkbox}
                                        id="all-day-checkbox"
                                        onCheckedChange={() =>
                                            setIsAllDay(!isAllDay)
                                        }
                                    />
                                    <Label
                                        className="cursor-pointer"
                                        htmlFor="all-day-checkbox"
                                    >
                                        {t(
                                            "dashboard.widgets.calendar.event.allDay",
                                        )}
                                    </Label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <MapPin
                            className={cn(
                                "mt-[-2px] h-4 w-4 flex-shrink-0",
                                toneClasses.icon,
                            )}
                        />
                        <Input
                            className={cn(
                                "flex items-center gap-2 rounded-md border px-3 py-1",
                                toneClasses.border,
                            )}
                            onChange={(e) => {
                                e.stopPropagation();
                                setLocation(e.target.value);
                            }}
                            value={location}
                        />
                    </div>
                    <EventAttendeesInput
                        attendees={attendees.filter(
                            (attendee) =>
                                attendee !==
                                event?.organizer?.emailAddress.address,
                        )}
                        setAttendees={(values) => {
                            let newAttendees = values;
                            if (event?.organizer?.emailAddress.address) {
                                newAttendees = [
                                    event?.organizer?.emailAddress.address,
                                    ...values,
                                ];
                            }
                            setAttendees(newAttendees);
                        }}
                        tone={tone}
                    />
                    {editor && (
                        <div className="flex items-start gap-2">
                            <NotebookPen
                                className={cn(
                                    "mt-2.5 h-4 w-4 flex-shrink-0",
                                    toneClasses.icon,
                                )}
                            />
                            <div className="flex w-full flex-col">
                                <RichTextToolbar editor={editor} tone={tone} />
                                <ScrollArea
                                    className={cn(
                                        "w-full rounded-bl-md rounded-br-md border border-t-0",
                                        toneClasses.border,
                                    )}
                                    variant="ghost"
                                >
                                    <div className="flex h-full max-h-[300px] w-full">
                                        <EditorContent
                                            className={EDITOR_CONTENT_CLASSES}
                                            editor={editor}
                                        />
                                    </div>
                                </ScrollArea>
                            </div>
                        </div>
                    )}
                </div>
            </ScrollArea>
            <div className="m-2 mx-4 flex items-center justify-end gap-2">
                {!event.isNewEvent() && (
                    <Button
                        className="border-primary-dark bg-black/30 text-white hover:bg-black/50"
                        disabled={isLoading}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsEditing?.(false);
                        }}
                    >
                        {t("dashboard.widgets.calendar.event.cancel")}
                    </Button>
                )}
                <Button
                    className="min-w-25 border-primary-dark text-primary-dark"
                    disabled={!subject || !startDate || !endDate || isLoading}
                    type="submit"
                    variant="outline"
                >
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        t("dashboard.widgets.calendar.event.save")
                    )}
                </Button>
            </div>
        </form>
    );
};

export default EventEditForm;
